import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CurrentUserType, AdminGrade } from '@/types';

interface AuthState {
    user: CurrentUserType | null;
    isAuthenticated: boolean;
    isLoading: boolean;
}

interface AuthActions {
    setUser: (user: CurrentUserType) => void;
    setLoading: (loading: boolean) => void;
    login: (user: CurrentUserType) => void;
    logout: () => void;
    updateUser: (updates: Partial<CurrentUserType>) => void;
}

type AuthStore = AuthState & {
    actions: AuthActions;
};

const initialState: AuthState = {
    user: null,
    isAuthenticated: false,
    isLoading: false,
};

export const useAuthStore = create<AuthStore>()(
    persist(
        (set, get) => ({
            ...initialState,
            actions: {
                setUser: (user) => set({ user, isAuthenticated: !!user }),

                setLoading: (loading) => set({ isLoading: loading }),

                login: (user) => {
                    // zustand persist와 동시에 개별 localStorage도 저장
                    localStorage.setItem('token', user.token || '');
                    localStorage.setItem('userId', user.userId || '');
                    localStorage.setItem('userName', user.userName || '');
                    localStorage.setItem('adminGrade', user.adminGrade || '');

                    set({
                        user,
                        isAuthenticated: true,
                        isLoading: false
                    });
                },

                logout: () => {
                    // 모든 localStorage 정리
                    localStorage.removeItem('token');
                    localStorage.removeItem('userId');
                    localStorage.removeItem('userName');
                    localStorage.removeItem('adminGrade');
                    localStorage.removeItem('savedUserId');
                    localStorage.removeItem('auth-storage'); // zustand persist 키도 삭제

                    set(initialState);
                    window.location.href = '/login';
                },

                updateUser: (updates) => {
                    const currentUser = get().user;
                    if (currentUser) {
                        const updatedUser = { ...currentUser, ...updates };

                        // localStorage도 함께 업데이트
                        if (updates.token) localStorage.setItem('token', updates.token);
                        if (updates.userId) localStorage.setItem('userId', updates.userId);
                        if (updates.userName) localStorage.setItem('userName', updates.userName);
                        if (updates.adminGrade) localStorage.setItem('adminGrade', updates.adminGrade);

                        set({
                            user: updatedUser
                        });
                    }
                },
            },
        }),
        {
            name: 'auth-storage', // localStorage key
            partialize: (state) => ({
                user: state.user,
                isAuthenticated: state.isAuthenticated
            }), // 저장할 부분만 선택
        }
    )
);

// 선택자 (Selectors)
export const useAuth = () => useAuthStore((state) => ({
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
}));

export const useAuthActions = () => useAuthStore((state) => state.actions);